import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { canManageAdminHierarchy, canSeeAdmin } from '../lib/permissions';

const fmt=v=>v?new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',year:'numeric',hour:'numeric',minute:'2-digit'}).format(new Date(v)):'—';
const providerName=p=>p==='google_forms'?'Google Forms':p.replaceAll('_',' ');

async function functionErrorMessage(error,data,fallback){
  if(data?.error)return data.error;
  try{const response=error?.context;if(response&&typeof response.clone==='function'){const payload=await response.clone().json();if(payload?.error)return payload.error;}}catch{}
  return error?.message||fallback;
}

export default function AdminIntegrationsPage(){
  const {role,workspace}=useAuth();
  const [rows,setRows]=useState([]),[error,setError]=useState(''),[busy,setBusy]=useState(''),[setup,setSetup]=useState(null),[message,setMessage]=useState('');
  const isSuperAdmin=canManageAdminHierarchy(role);

  async function refresh(){
    if(!canSeeAdmin(role)||!workspace?.id)return;
    setError('');
    const {data,error:e}=await supabase.from('integration_connections').select('id,provider,status,config,created_at,updated_at').eq('workspace_id',workspace.id).order('provider');
    if(e)setError(e.message);else setRows(data||[]);
  }

  useEffect(()=>{refresh()},[role,workspace?.id]);
  if(!canSeeAdmin(role))return <Navigate to="/" replace/>;

  async function manageGoogleForms(action){
    setBusy(action);setError('');setMessage('');setSetup(null);
    const {data,error:e}=await supabase.functions.invoke('google-forms-control',{body:{workspaceId:workspace.id,action}});
    setBusy('');
    if(e||data?.error){setError(await functionErrorMessage(e,data,'Google Forms integration action failed.'));return;}
    if(action==='connect'){
      setSetup({secret:data.secret,webhookUrl:data.webhookUrl});
      setMessage('Google Forms integration activated. Copy the secret now; it will not be shown again.');
    }else setMessage('Google Forms integration disconnected. Existing form history was preserved and the previous secret was revoked.');
    await refresh();
  }

  const googleForms=rows.find(row=>row.provider==='google_forms');
  const otherRows=rows.filter(row=>row.provider!=='google_forms');

  return <section className="page-stack">
    <div><h1 className="page-title">Integrations</h1><p className="page-description">Connection health and configuration status. Secrets are never stored or displayed in plaintext after setup.</p></div>
    {error&&<div className="error-message">{error}</div>}
    {message&&<div className="success-message">{message}</div>}

    <div className="panel integration-manage-card">
      <div className="integration-row integration-row-managed">
        <div><strong>Google Forms</strong><span>Apps Script webhook for qualification form submissions.</span><small>Updated {fmt(googleForms?.updated_at)}</small></div>
        <span className={`integration-status ${googleForms?.status||'inactive'}`}>{googleForms?.status||'inactive'}</span>
        {isSuperAdmin&&<div className="integration-actions">
          <button className="secondary-button" disabled={Boolean(busy)} onClick={()=>manageGoogleForms('connect')}>{busy==='connect'?'Generating…':googleForms?.status==='active'?'Reconnect':'Connect'}</button>
          {googleForms?.status==='active'&&<button className="text-button danger-text" disabled={Boolean(busy)} onClick={()=>manageGoogleForms('disconnect')}>{busy==='disconnect'?'Disconnecting…':'Disconnect'}</button>}
        </div>}
      </div>
      {!isSuperAdmin&&<div className="report-note"><strong>Super Admin only</strong><span>Only Super Admins can connect, reconnect, or disconnect integrations.</span></div>}
      {isSuperAdmin&&setup&&<div className="integration-setup-box">
        <div><strong>One-time setup credentials</strong><span>Copy both values into the Apps Script attached to your Google Form. The secret will not be retrievable after you leave this screen.</span></div>
        <label><span>Webhook URL</span><input className="text-input" readOnly value={setup.webhookUrl}/></label>
        <label><span>LEAD_TRACKER_WEBHOOK_SECRET</span><input className="text-input integration-secret" readOnly value={setup.secret}/></label>
        <div className="integration-copy-actions"><button className="secondary-button" onClick={()=>navigator.clipboard?.writeText(setup.webhookUrl)}>Copy webhook URL</button><button className="primary-button" onClick={()=>navigator.clipboard?.writeText(setup.secret)}>Copy secret</button></div>
      </div>}
    </div>

    {otherRows.length>0&&<div className="panel">{otherRows.map(row=><div className="integration-row" key={row.id}>
      <div><strong>{providerName(row.provider)}</strong><span>{row.config?.mode?row.config.mode.replaceAll('_',' '):'Configured integration'}</span><small>Updated {fmt(row.updated_at)}</small></div>
      <span className={`integration-status ${row.status}`}>{row.status}</span>
    </div>)}</div>}

    <div className="panel report-note"><strong>Google Forms</strong><span>The live receiver uses the dedicated webhook Edge Function and shared-secret validation. Disconnecting revokes the form connection without deleting historical submissions.</span></div>
  </section>;
}
